import React from 'react';
import { ValueType } from 'react-select';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { isMobile } from 'react-device-detect';

import MIcon from 'app/ui/MIcon/MIcon';
import MButton from 'app/ui/MButton/MButton';
import MNewSelect from 'app/ui/MNewSelect/MNewSelect';
import MFormDatetime from 'app/ui/MForm/MFormFields/MFormDatetime/MFormDatetime';

import { FormikContextType } from 'formik';

import setFieldProps from 'app/utils/setFieldProps';

import classes from './ByLine.module.scss';
import { TimeFormats } from 'app/constants/TimeFormat';
import { ByLineForm, IDropOff, IPickUpTime, IPickUp, IInitTimes } from './ByLine.model';
import { SelectOption } from 'app/ui/MNewSelect/MNewSelect.model';

interface Props {
  form: FormikContextType<ByLineForm>;
  shuttleType: 'simple' | 'fixable';
  optionsArea: SelectOption[];
  optionsLine: SelectOption[];
  optionsDirection: SelectOption[];
  optionsPickupStops: SelectOption<IPickUp>[];
  optionsPickUpStopsTimes: SelectOption<IPickUpTime>[];
  optionsDropoffStop: SelectOption<IDropOff>[];
  disabledWeekDaysState: number[];
  holidays: moment.Moment[];
  initialStop: string;
  initTimePlaceHolder: string;
  isExoRoutingType: boolean;
  initalTimesOptions: SelectOption<IInitTimes>[];
  onAreaChange: (value: ValueType<SelectOption, false>) => void;
  onLineChange: (value: ValueType<SelectOption, false>) => void;
  onDirectionChange: (value: ValueType<SelectOption, false>) => void;
  onDateChange: (value: moment.Moment | null) => void;
  onPickUpChange: (value: ValueType<SelectOption<IPickUp>, false>) => void;
  onPickUpTimeChange: (value: SelectOption<IPickUpTime> | null) => void;
  onDropOffChange: (value: ValueType<SelectOption<IDropOff>, false>) => void;
}

const ByLineView: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const { t } = useTranslation();

  return (
    <form className={classes['container']} onSubmit={props.form.handleSubmit}>
      <div className={classes['content']}>
        <div className={classes['fields']}>
          {props.optionsArea.length > 0 ? (
            <MNewSelect
              placeholder={t('reservationBar.formLine.area')}
              className={classes['select']}
              options={props.optionsArea}
              hideKeyboard={isMobile}
              icon="cilMap"
              {...setFieldProps(props.form, 'area', {
                beforeChange: (event: ValueType<SelectOption, false>) => event?.value || null,
                onChange: props.onAreaChange,
              })}
            ></MNewSelect>
          ) : null}
          <MNewSelect
            placeholder={t('reservationBar.formLine.shuttles')}
            className={classes['select']}
            options={props.optionsLine}
            hideKeyboard={isMobile}
            icon="cilBus"
            {...setFieldProps(props.form, 'line', {
              beforeChange: (event: ValueType<SelectOption, false>) => event?.value || null,
              onChange: props.onLineChange,
            })}
          ></MNewSelect>
          <MNewSelect
            placeholder={t('reservationBar.formLine.direction')}
            className={classes['select']}
            options={props.optionsDirection}
            hideKeyboard={isMobile}
            icon="cilSwapHorizontal"
            {...setFieldProps(props.form, 'direction', {
              beforeChange: (event: ValueType<SelectOption, false>) => event?.value || null,
              onChange: props.onDirectionChange,
            })}
          ></MNewSelect>
          <div className={classes['datePicker']}>
            <MIcon className={classes['datePicker__icon']} name="cilCalendar"></MIcon>
            <MFormDatetime
              {...setFieldProps(props.form, 'date', {
                beforeChange: (event: moment.Moment | null) => event || null,
                onChange: props.onDateChange,
              })}
              dateFormat={TimeFormats.UIFormatDatePickerWithoutTime}
              weekDays={props.disabledWeekDaysState}
              holidays={props.holidays}
              placeholder={t('reservationBar.formLine.date')}
              minDate={moment()}
            ></MFormDatetime>
          </div>
        </div>
        {props.isExoRoutingType ?
          <div className={classes['fields']}>
            <div className={classes['initStop']}>
              <MIcon name="cilLocationPin" className={classes['icon']}></MIcon><span>{props.initialStop}</span>
            </div>
            <MNewSelect
              placeholder={props.initTimePlaceHolder}
              className={`${classes['selectInput']} ${classes['selectInput--time']}`}
              options={props.initalTimesOptions}
              icon="cilClock"
              hideKeyboard={isMobile}
              {...setFieldProps(props.form, 'initialTime', {
                beforeChange: (event: ValueType<SelectOption<IInitTimes>, false>) => event?.value || null,
              })}
            ></MNewSelect>
          </div> :
          null
        }

        <div className={classes['fields']}>
          <MNewSelect
            placeholder={t('reservationBar.formLine.pickup')}
            className={classes['selectInput']}
            options={props.optionsPickupStops}
            icon="cilLocationPin"
            hideKeyboard={isMobile}
            {...setFieldProps(props.form, 'pickUp', {
              beforeChange: (event: ValueType<SelectOption<IPickUp>, false>) => event?.value || null,
              onChange: props.onPickUpChange,
            })}
          ></MNewSelect>
          {props.shuttleType === 'simple' ? (
            <MNewSelect
              placeholder={t('reservationBar.formLine.pickUpTime')}
              className={`${classes['selectInput']} ${classes['selectInput--time']}`}
              options={props.optionsPickUpStopsTimes}
              icon="cilClock"
              hideKeyboard={isMobile}
              {...setFieldProps(props.form, 'pickUpTime', {
                beforeChange: (event: ValueType<SelectOption<IPickUpTime>, false>) => event?.value || null,
                onChange: props.onPickUpTimeChange,
              })}
            ></MNewSelect>
          ) : null}

          <MNewSelect
            placeholder={t('reservationBar.formLine.dropoff')}
            className={classes['selectInput']}
            options={props.optionsDropoffStop}
            hideKeyboard={isMobile}
            icon="cilLocationPin"
            {...setFieldProps(props.form, 'dropOff', {
              beforeChange: (event: ValueType<SelectOption<IDropOff>, false>) => event?.value || null,
              onChange: props.onDropOffChange,
            })}
          ></MNewSelect>
          {props.shuttleType === 'simple' ? (
            <MNewSelect
              placeholder={t('reservationBar.formLine.dropOffTime')}
              className={`${classes['selectInput']} ${classes['selectInput--time']}`}
              icon="cilClock"
              value={props.form.values.dropOffTime}
              hideKeyboard={isMobile}
              options={[{ value: props.form.values.dropOffTime || '', label: props.form.values.dropOffTime || '' }]}
            ></MNewSelect>
          ) : (
            <div className={classes['datePicker']}>
              <MIcon className={classes['datePicker__icon']} name="cilCalendar"></MIcon>
              <MFormDatetime
                name="dropTime"
                value={props.form.values.dropOffTime ? moment(props.form.values.dropOffTime, TimeFormats.Time24) : null}
                onChange={(date: moment.Moment | null) => props.form.setFieldValue('dropOffTime', date?.format(TimeFormats.Time24))}
                dateFormat={TimeFormats.Time24}
                showTimeSelect
                showTimeSelectOnly
                minDate={moment()}
                placeholder={t('reservationBar.formLine.dropOffTime')}
              ></MFormDatetime>
            </div>
          )}
        </div>
      </div>

      <MButton
        className={classes['btn']}
        type="submit"
        disabled={!props.form.values.date || !props.form.values.pickUp || !props.form.values.dropOff}
      >
        {t('reservationBar.formLine.btnSubmit')}
      </MButton>
    </form>
  );
};

ByLineView.displayName = 'ByLineView';
ByLineView.defaultProps = {};

export default ByLineView;
